'use client';

import React from 'react';
import HeaderV2 from '../global/partials/HeaderV2';
import Footer from '../global/partials/Footer';
import { PageAssetsSchema } from '@/sanity/pages/PageAssetsTemplate';
import HeroSection from '../pageComponents/assets/HeroSection';
import PlanSection from '../pageComponents/assets/Plan';
import What from '../pageComponents/assets/What';
import PlatformSection from '../pageComponents/assets/Platform';
import RewardsSection from '../pageComponents/assets/Rewards';
import BestPerformers from '../pageComponents/assets/Best';
import LastCtaAssets from '../pageComponents/assets/LastCTAAssets';
import heroStatue1 from '@/public/affiliate-overview/statue.png';
import ethBTC from '@/public/affiliate-overview/eth-btc.svg';
import solana from '@/public/affiliate-overview/solana.svg';
import heroBG from '@/public/affiliate-overview/heroBg.png';
import lines from '@/public/affiliate-overview/lines.svg';
import lilIcon from '@/public/affiliate-overview/lil-icon.svg';
import iq from '@/public/affiliate-overview/iq-and-you.svg';
import growthCardBG from '@/public/affiliate-overview/growth-card-bg.png';
import logoBlack from '@/public/global/logo-black.svg';
import growthDots from '@/public/affiliate-overview/growth-dots.png';
import footerDots from '@/public/affiliate-overview/footer-dots.png';
import doubleStatues from '@/public/affiliate-overview/double-statues.png';
import lastBG from '@/public/affiliate-overview/last-bg.png';
import lastStatue from '@/public/affiliate-overview/last-statue.png';
import dotsBG from '@/public/affiliate-overview/dots-bg.png';
import stepCardBG from '@/public/affiliate-overview/step-card-bg.png';

import Image from 'next/image';
import { motion } from 'framer-motion';
import { PortableText } from '@portabletext/react';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { CTAButton } from '@/shadcn/ui/button';
import { GROQImageLoader } from '@/sanity/lib/definitions';
import { resolvePageLink } from '@/sanity/globals/CTA';
import { rewriteOfferPercent } from '@/lib/affiliateOfferCopy';
import { usePathname } from 'next/navigation';
import {
  resolveI18nText,
  resolveI18nRichtext,
  NON_DEFAULT_LANGUAGES,
  DEFAULT_LANGUAGE,
} from '@/sanity/globals/I18n';
const ease = [0.25, 0.1, 0.25, 1] as const;

// ── Page ───────────────────────────────────────────────────────────────────────

export default function PageAssetsJSX({ data: rawData }: { data: PageAssetsSchema }) {
  // The offer copy takes the % the plan cards are priced with.
  const percent = rawData?.planSectionV3?.discountPercent;
  const data = {
    ...rawData,
    heroChip: rewriteOfferPercent(rawData?.heroChip, percent),
    lastChip: rewriteOfferPercent(rawData?.lastChip, percent),
  };
  const pathname = usePathname();
  const language =
    NON_DEFAULT_LANGUAGES.find(
      (l) => pathname.startsWith(`/${l}/`) || pathname === `/${l}`,
    ) ?? DEFAULT_LANGUAGE;

  const footer = data.settings?.footer;
  const disclaimerText = resolveI18nRichtext(
    footer?.disclaimer,
    footer?.disclaimerI18n,
    language,
  );
  const footerLegalLinks =
    footer?.navColumns?.find((col) => col.heading === 'POLICY')?.links ?? [];

  return (
    <>
      {data?.settings?.header && (
        <HeaderV2
          data={data.settings.header}
          headerType="main"
          headerStyle="light"
          language={data.settings?.language}
        />
      )}

      <HeroSection data={data} />
      <PlanSection data={data} />
      <What data={data} />
      <PlatformSection data={data} />
      <RewardsSection data={data} />
      <BestPerformers data={data} />
      <LastCtaAssets data={data} />
      {(data?.footerDisclaimerCopyright ||
        footerLegalLinks.length > 0 ||
        disclaimerText) && (
        <section className="relative w-full bg-[#F4F4F4] py-[6rem] px-[3rem] lg:py-[8rem] lg:px-[4rem]">
          <Image
            src={footerDots}
            alt=""
            className="object-cover absolute inset-0 w-full h-full pointer-events-none mix-blend-exclusion"
          />
          <div className="container flex flex-col mx-auto gap-[4rem] w-[min(140rem)]">
            {(data?.footerDisclaimerCopyright ||
              footerLegalLinks.length > 0) && (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.35 }}
                transition={{ duration: 0.6, ease }}
                className="flex relative flex-col justify-between items-center lg:flex-row gap-[2rem] lg:h-[3.5rem]"
              >
                {data?.footerDisclaimerCopyright && (
                  <p className="m-0 text-[#103037]/70 text-[1.75rem] lg:text-[2rem]">
                    {data.footerDisclaimerCopyright}
                  </p>
                )}
                <div className="lg:absolute lg:hidden block lg:top-1/2 lg:left-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2">
                  <Image
                    src={logoBlack}
                    alt=""
                    className="pointer-events-none"
                  />
                </div>
                {footerLegalLinks.length > 0 && (
                  <ul className="flex flex-wrap items-center p-0 m-0 list-none gap-x-[1rem] gap-y-[1rem]">
                    {footerLegalLinks.map((item, idx) => {
                      const href = resolvePageLink(item.link, language) ?? '#';
                      const openInNewTab = item.link?.openInNewTab;
                      const isLast = idx === footerLegalLinks.length - 1;
                      return (
                        <React.Fragment key={idx}>
                          <li className="m-0">
                            <a
                              href={href}
                              target={openInNewTab ? '_blank' : undefined}
                              rel={
                                openInNewTab ? 'noopener noreferrer' : undefined
                              }
                              className="transition-all hover:underline text-black/80 text-[1.75rem] underline-offset-4 font-geist lg:text-[2rem]"
                            >
                              {resolveI18nText(item.title, item.i18n, language)}
                            </a>
                          </li>
                          {!isLast && (
                            <p className="m-0 text-black/80 text-[1.75rem] lg:text-[2rem]">
                              /
                            </p>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </ul>
                )}
              </motion.div>
            )}
            <div className="w-full border-t h-[.125rem] border-t-black/10" />
            {disclaimerText && (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, ease, delay: 0.1 }}
                className="richtext richtext-center prose-p:text-black/60 prose-p:text-[1.75rem] prose-p:leading-[150%] prose-p:font-geist"
              >
                <PortableText
                  value={disclaimerText}
                  components={richTextComponent}
                />
              </motion.div>
            )}
          </div>
        </section>
      )}
    </>
  );
}
